/**
 * Culture Syringes Management Hook
 *
 * Third-party liquid culture syringes purchased from external suppliers.
 * Used to inoculate in-house LC jars.
 *
 * Provides hooks for:
 * - Fetching syringes list
 * - Creating new syringes
 * - Updating existing syringes
 * - Using syringes (volume deduction)
 * - Disposing syringes
 */

import { useState, useEffect, useCallback } from 'react'
import { toLocalDateString } from '../utils/dateUtils'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/culture-syringes`

/**
 * Call the culture-syringes edge function
 */
async function callSyringesFunction(action, data = {}) {
  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'apikey': SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...data }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed')
  }

  return result.data
}

/**
 * Main hook for culture syringes management
 *
 * @param {Object} options - Hook options
 * @param {string} options.status - Filter by status
 * @param {string} options.strain_id - Filter by strain
 * @param {boolean} options.includeExhausted - If true, include exhausted/disposed syringes
 * @returns {Object} - syringes, isLoading, error, and mutation functions
 */
export function useCultureSyringes(options = {}) {
  const { status, strain_id, includeExhausted = false } = options

  const [syringes, setSyringes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Fetch syringes from the API
   */
  const fetchSyringes = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const params = {
        ...(status && { status }),
        ...(strain_id && { strain_id }),
        include_exhausted: includeExhausted,
      }
      const data = await callSyringesFunction('list', params)

      setSyringes(data || [])
    } catch (err) {
      console.error('Error fetching syringes:', err)
      setError(err.message)
      setSyringes([])
    } finally {
      setIsLoading(false)
    }
  }, [status, strain_id, includeExhausted])

  /**
   * Create a new syringe
   *
   * @param {Object} syringeData - Syringe data
   * @param {string} syringeData.supplier_batch_code - Supplier's batch code (required)
   * @param {string} syringeData.strain_id - Strain ID (required)
   * @param {string} syringeData.supplier_id - Supplier ID (required)
   * @param {number} syringeData.volume_ml - Volume in ml (required)
   * @param {string} syringeData.received_date - Date received
   * @param {string} syringeData.storage_location - Storage location
   * @param {string} syringeData.notes - Additional notes
   * @returns {Promise<Object>} - Created syringe
   */
  const createSyringe = useCallback(async (syringeData) => {
    try {
      setError(null)
      const newSyringe = await callSyringesFunction('create', syringeData)

      // Add to local state
      setSyringes(prev => [newSyringe, ...prev])

      return newSyringe
    } catch (err) {
      console.error('Error creating syringe:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Update an existing syringe
   *
   * @param {string} id - Syringe ID
   * @param {Object} updates - Fields to update (storage_location, notes, status)
   * @returns {Promise<Object>} - Updated syringe
   */
  const updateSyringe = useCallback(async (id, updates) => {
    try {
      setError(null)
      const updatedSyringe = await callSyringesFunction('update', { id, ...updates })

      // Update local state
      setSyringes(prev =>
        prev.map(s => s.id === id ? updatedSyringe : s)
      )

      return updatedSyringe
    } catch (err) {
      console.error('Error updating syringe:', err)
      setError(err.message)
      throw err
    }
  }, [])

  /**
   * Use syringe: deduct volume when inoculating an LC jar
   *
   * @param {string} id - Syringe ID
   * @param {number} volume_used_ml - Volume used in ml
   * @returns {Promise<Object>} - Updated syringe
   */
  const useSyringe = useCallback(async (id, volume_used_ml) => {
    try {
      setError(null)
      const updatedSyringe = await callSyringesFunction('use', { id, volume_used_ml })

      // Update local state
      setSyringes(prev => {
        // If syringe is now exhausted and we're not including exhausted, remove it
        if (updatedSyringe.status === 'exhausted' && !includeExhausted) {
          return prev.filter(s => s.id !== id)
        }
        return prev.map(s => s.id === id ? updatedSyringe : s)
      })

      return updatedSyringe
    } catch (err) {
      console.error('Error using syringe:', err)
      setError(err.message)
      throw err
    }
  }, [includeExhausted])

  /**
   * Dispose of a syringe
   *
   * @param {string} id - Syringe ID
   * @returns {Promise<Object>} - Disposed syringe
   */
  const disposeSyringe = useCallback(async (id) => {
    try {
      setError(null)
      const disposedSyringe = await callSyringesFunction('dispose', { id })

      // Update local state - remove if not including exhausted
      if (!includeExhausted) {
        setSyringes(prev => prev.filter(s => s.id !== id))
      } else {
        setSyringes(prev =>
          prev.map(s => s.id === id ? disposedSyringe : s)
        )
      }

      return disposedSyringe
    } catch (err) {
      console.error('Error disposing syringe:', err)
      setError(err.message)
      throw err
    }
  }, [includeExhausted])

  /**
   * Delete a syringe (hard delete - only for unused syringes)
   *
   * @param {string} id - Syringe ID
   * @returns {Promise<void>}
   */
  const deleteSyringe = useCallback(async (id) => {
    try {
      setError(null)
      await callSyringesFunction('delete', { id })

      // Remove from local state
      setSyringes(prev => prev.filter(s => s.id !== id))
    } catch (err) {
      console.error('Error deleting syringe:', err)
      setError(err.message)
      throw err
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchSyringes()
  }, [fetchSyringes])

  return {
    syringes,
    isLoading,
    error,
    createSyringe,
    updateSyringe,
    useSyringe,
    disposeSyringe,
    deleteSyringe,
    refetch: fetchSyringes,
  }
}

/**
 * Hook for fetching a single syringe with full details
 *
 * @param {string} syringeId - Syringe ID
 * @returns {Object} - syringe, isLoading, error, refetch
 */
export function useCultureSyringe(syringeId) {
  const [syringe, setSyringe] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSyringe = useCallback(async () => {
    if (!syringeId) {
      setSyringe(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const data = await callSyringesFunction('get', { id: syringeId })
      setSyringe(data)
    } catch (err) {
      console.error('Error fetching syringe:', err)
      setError(err.message)
      setSyringe(null)
    } finally {
      setIsLoading(false)
    }
  }, [syringeId])

  useEffect(() => {
    fetchSyringe()
  }, [fetchSyringe])

  return { syringe, isLoading, error, refetch: fetchSyringe }
}

/**
 * Hook for fetching all available syringes for LC inoculation.
 * Returns syringes grouped by strain for UI display.
 *
 * @returns {Object} { syringes, syringesByStrain, isLoading, error }
 */
export function useAvailableSyringes() {
  const [syringes, setSyringes] = useState([])
  const [syringesByStrain, setSyringesByStrain] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchSyringes = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const data = await callSyringesFunction('list', {
        include_exhausted: false,
      })

      // Filter to only syringes with remaining volume and not expired
      const today = toLocalDateString(new Date())
      const available = (data || []).filter(s =>
        s.volume_remaining_ml > 0 &&
        s.expiry_date >= today &&
        s.status !== 'exhausted' &&
        s.status !== 'disposed'
      )

      setSyringes(available)

      // Group by strain for UI
      const grouped = available.reduce((acc, syringe) => {
        const strainName = syringe.strain?.name || 'Unknown'
        if (!acc[strainName]) {
          acc[strainName] = []
        }
        acc[strainName].push(syringe)
        return acc
      }, {})
      setSyringesByStrain(grouped)
    } catch (err) {
      console.error('Error fetching available syringes:', err)
      setError(err.message)
      setSyringes([])
      setSyringesByStrain({})
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSyringes()
  }, [fetchSyringes])

  return { syringes, syringesByStrain, isLoading, error, refetch: fetchSyringes }
}

export default useCultureSyringes
